import { useState } from "react";
import { useAuth } from "@/lib/auth";
import { Loader2, AlertCircle } from "lucide-react";
import { InstallAppButton } from "@/components/shared/InstallAppButton";
import { SandboxBanner } from "@/components/shared/SandboxBanner";

export default function LoginPage() {
  const { login } = useAuth();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(true);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) return;
    setError("");
    setLoading(true);
    try {
      await login(username, password, rememberMe);
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed. Check your Odoo credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] flex flex-col items-center justify-center px-4" data-testid="login-page">
      <div className="fixed top-0 inset-x-0"><SandboxBanner /></div>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src="/stancor-brand-logo.png" alt="Stancor Tubulars" className="h-24 mx-auto mb-3 object-contain" data-testid="login-logo" />
          <p className="text-sm text-[#9CA3AF] mt-1 tracking-widest uppercase">Reporting System</p>
        </div>

        {/* Login Card */}
        <div className="bg-[#1A1A1A] border border-[#333] rounded-lg p-6 shadow-xl" data-testid="login-form">
          <h2 className="text-lg font-semibold text-white mb-1 uppercase tracking-wide">
            Sign In
          </h2>
          <p className="text-xs text-[#9CA3AF] mb-5">Use your Odoo ERP credentials</p>

          {error && (
            <div className="bg-red-900/30 border border-red-800 rounded-sm px-3 py-2.5 mb-4" data-testid="login-error">
              <div className="flex items-start gap-2">
                <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
                <span className="text-xs text-red-300 leading-relaxed">{error}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
                Odoo Username / Email
              </label>
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                placeholder="your.email@stancor.in"
                className="w-full bg-[#0D0D0D] border border-[#333] rounded-sm px-3 py-2.5 text-sm text-white outline-none focus:border-[#E32C17] focus:ring-1 focus:ring-[#E32C17]/20 placeholder:text-[#666]"
                autoComplete="username" autoFocus data-testid="login-username" />
            </div>
            <div>
              <label className="text-xs font-semibold text-[#9CA3AF] uppercase tracking-wider block mb-1.5">
                Password / API Key
              </label>
              <input type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                placeholder="Your Odoo password or API key"
                className="w-full bg-[#0D0D0D] border border-[#333] rounded-sm px-3 py-2.5 text-sm text-white outline-none focus:border-[#E32C17] focus:ring-1 focus:ring-[#E32C17]/20 placeholder:text-[#666]"
                autoComplete="current-password" data-testid="login-password" />
              <p className="text-[10px] text-[#555] mt-1">2FA enabled? Use your Odoo API Key (Settings &gt; API Keys)</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none" data-testid="remember-me">
              <input type="checkbox" checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)}
                className="w-4 h-4 rounded border-[#333] text-[#E32C17] focus:ring-[#E32C17]/20 cursor-pointer bg-[#0D0D0D]" />
              <span className="text-xs text-[#9CA3AF]">Stay signed in for 30 days</span>
            </label>
            <button type="submit" disabled={loading || !username || !password}
              className="w-full bg-[#E32C17] hover:bg-[#FF3D28] text-white rounded-sm py-2.5 text-sm font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ transition: "background-color 0.15s" }} data-testid="login-submit">
              {loading ? <Loader2 size={16} className="animate-spin" /> : null}
              {loading ? "Signing in..." : "Sign In"}
            </button>
          </form>

          <InstallAppButton dark />

          <p className="text-[10px] text-[#666] text-center mt-5">
            Powered by Odoo ERP Authentication
          </p>
        </div>
      </div>
    </div>
  );
}
